import "../Styles/Study.css";
import { Link } from "react-router-dom";
import { MdSchool, MdPublic, MdOutlineMenuBook, MdWork, MdCheckCircle, MdDescription, MdGroups } from 'react-icons/md';
import { FaHandshake, FaPlane } from 'react-icons/fa';
import { Helmet } from "react-helmet";
import { PageHero, CtaBand } from "./PageParts";
import { motion, AnimatePresence } from "motion/react";
import { useState } from "react";
import Reveal, { RevealGroup, RevealItem } from "./Reveal";

export default function Study() {
  const [openFaq, setOpenFaq] = useState(0);

  const studyFaqs = [
    { q: "Which is the best country to study abroad for Indian students in 2026?", a: "Canada is the most popular choice due to its clear PR pathway after graduation (PGWP up to 3 years), affordable tuition compared to the US, and multicultural environment. Germany is ideal for budget-conscious students as public universities charge no tuition fees." },
    { q: "What is the minimum IELTS score required to study abroad?", a: "Most universities require a minimum IELTS score of 6.0–6.5 for undergraduate and 6.5–7.0 for postgraduate programs. Top universities in the UK and USA may require 7.0+. Canada generally accepts 6.5 overall with no band below 6.0." },
    { q: "Can I work while studying abroad?", a: "Yes. Canada allows 24 hours/week during semesters and full-time during breaks. Australia allows 48 hours per fortnight. Germany allows 120 full days or 240 half days per year. The UK allows 20 hours/week during term time." },
    { q: "How much does it cost to study in Canada for Indian students?", a: "Tuition fees range from CAD 15,000 to CAD 35,000 per year. Living expenses add approximately CAD 10,000–15,000 per year. Total annual cost is typically CAD 25,000–50,000." },
    { q: "What is a Post-Graduate Work Permit (PGWP) in Canada?", a: "A PGWP allows international graduates from a Canadian Designated Learning Institution (DLI) to work in Canada for up to 3 years. It is an open work permit and a key step toward Canadian PR through the Canadian Experience Class (CEC)." },
    { q: "Is Germany really free for Indian students?", a: "Yes, public universities in Germany charge no tuition fees for international students. Students only pay a semester contribution of EUR 150–350 covering admin fees and a public transport pass. You need a blocked account with EUR 11,208 for your visa." },
  ];

  return (
    <div className="study-page-modern ve-scope">
      <Helmet>
        <title>Study Abroad Consultants | Universities in Canada, USA, UK, Aus</title>
        <meta name="description" content="Connect with 500+ partner universities worldwide. Get expert counseling, scholarship guidance, and visa support for Canada, USA, UK, and Australia." />
        <link rel="canonical" href="https://veabroad.com/study" />
        <meta property="og:title" content="Study Abroad Consultants | Universities in Canada, USA, UK, Aus" />
        <meta property="og:description" content="Connect with 500+ partner universities worldwide. Get expert counseling, scholarship guidance, and visa support for Canada, USA, UK, and Australia." />
        <meta property="og:url" content="https://veabroad.com/study" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://veabroad.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Study Abroad Consultants | Universities in Canada, USA, UK, Aus" />
        <meta name="twitter:description" content="Connect with 500+ partner universities worldwide. Get expert counseling, scholarship guidance, and visa support for Canada, USA, UK, and Australia." />
        <meta name="twitter:image" content="https://veabroad.com/og-image.jpg" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Study Abroad Consulting",
            "provider": {
              "@type": "EducationalOrganization",
              "name": "Vidya Education Abroad",
              "url": "https://veabroad.com/"
            },
            "areaServed": "IN",
            "description": "Comprehensive counseling and support for students seeking higher education in Canada, USA, UK, Australia, and Germany."
          })}
        </script>
      </Helmet>

      {/* Cinematic Hero */}
      <PageHero
        image="/journey_campus_bg.webp"
        eyebrow="Study abroad"
        title={<>Study where the degree <em>actually takes you</em> somewhere.</>}
        lede="Course and university selection built around your marks, your budget and where you want to end up — then the SOP, the scholarship mapping and the visa filing that get you there."
        ctas={[
          { to: "/consultationForm", label: "Check your eligibility" },
          { href: "#destinations", label: "Explore destinations", variant: "ghost" },
        ]}
        stats={[
          { figure: "1,000+", label: "Students placed" },
          { figure: "500+", label: "Partner universities" },
          { figure: "95%", label: "Visa success rate" },
        ]}
      />

      {/* Modern Features Grid */}
      <section className="py-6 section-features">
        <div className="container">
          <Reveal as="h2" direction="up" className="text-center mb-5 display-5 fw-bold">
            Why Study Abroad?
          </Reveal>
          <RevealGroup as="div" className="row g-4" stagger={0.12}>
            <RevealItem as="div" className="col-lg-3 col-md-6">
              <div className="feature-card-modern va-hover-lift p-4 h-100">
                <div className="feature-icon-wrapper mb-4">
                  <MdSchool className="text-success" />
                </div>
                <h4>World-Class Education</h4>
                <p className="text-muted">Access globally recognized universities with cutting-edge research facilities and innovative teaching.</p>
              </div>
            </RevealItem>
            <RevealItem as="div" className="col-lg-3 col-md-6">
              <div className="feature-card-modern va-hover-lift p-4 h-100">
                <div className="feature-icon-wrapper mb-4">
                  <MdWork className="text-primary" />
                </div>
                <h4>Better Career Prospects</h4>
                <p className="text-muted">International degrees open doors to global career opportunities with higher earning potential.</p>
              </div>
            </RevealItem>
            <RevealItem as="div" className="col-lg-3 col-md-6">
              <div className="feature-card-modern va-hover-lift p-4 h-100">
                <div className="feature-icon-wrapper mb-4">
                  <MdGroups className="text-warning" />
                </div>
                <h4>Cultural Exposure</h4>
                <p className="text-muted">Experience diverse cultures, build international networks, and develop a global perspective.</p>
              </div>
            </RevealItem>
            <RevealItem as="div" className="col-lg-3 col-md-6">
              <div className="feature-card-modern va-hover-lift p-4 h-100">
                <div className="feature-icon-wrapper mb-4">
                  <MdPublic className="text-info" />
                </div>
                <h4>Post-Study Pathways</h4>
                <p className="text-muted">Many countries offer work permits after graduation, providing direct pathways to residency.</p>
              </div>
            </RevealItem>
          </RevealGroup>
        </div>
      </section>

      {/* Reveal Cards Destinations */}
      <section id="destinations" className="py-6 bg-light-mesh va-blob-field-wrap position-relative">
        <div className="va-blob-field" aria-hidden="true">
          <div className="va-blob va-blob-mint" style={{ top: "5%", left: "80%", width: "300px", height: "300px" }}></div>
          <div className="va-blob va-blob-teal" style={{ top: "70%", left: "-5%", width: "260px", height: "260px" }}></div>
        </div>
        <div className="container position-relative">
          <Reveal as="h2" direction="up" className="text-center mb-5 display-5 fw-bold">
            Top Study Destinations
          </Reveal>
          <RevealGroup as="div" className="row g-4" stagger={0.08}>
            <RevealItem as="div" direction="zoom" className="col-lg-4 col-md-6">
              <div className="reveal-card va-hover-lift">
                <div className="reveal-bg" style={{backgroundImage: "url('https://images.unsplash.com/photo-1503614472-8c93d56e92ce?w=500&h=600&fit=crop')"}}></div>
                <div className="reveal-overlay"></div>
                <div className="reveal-content-front">
                  <div className="flag-icon mb-2">🇨🇦</div>
                  <h3>Canada</h3>
                </div>
                <div className="reveal-content-hover">
                  <h3>Canada</h3>
                  <p>Top-ranked universities and up to 3 years PGWP.</p>
                  <ul className="list-unstyled small mt-3">
                    <li><MdCheckCircle className="text-success me-2" />High Quality Education</li>
                    <li><MdCheckCircle className="text-success me-2" />Multicultural Society</li>
                    <li><MdCheckCircle className="text-success me-2" />Clear PR Pathway</li>
                  </ul>
                  <Link to="/consultationForm" className="modern-btn primary sm mt-3 w-100">Apply Now</Link>
                </div>
              </div>
            </RevealItem>

            <RevealItem as="div" direction="zoom" className="col-lg-4 col-md-6">
              <div className="reveal-card va-hover-lift">
                <div className="reveal-bg" style={{backgroundImage: "url('https://images.unsplash.com/photo-1523482580672-f109ba8cb9be?w=500&h=600&fit=crop')"}}></div>
                <div className="reveal-overlay"></div>
                <div className="reveal-content-front">
                  <div className="flag-icon mb-2">🇦🇺</div>
                  <h3>Australia</h3>
                </div>
                <div className="reveal-content-hover">
                  <h3>Australia</h3>
                  <p>World-class research facilities and excellent climate.</p>
                  <ul className="list-unstyled small mt-3">
                    <li><MdCheckCircle className="text-success me-2" />Research Excellence</li>
                    <li><MdCheckCircle className="text-success me-2" />2-4 years Post-Study Work</li>
                    <li><MdCheckCircle className="text-success me-2" />Great Lifestyle</li>
                  </ul>
                  <Link to="/consultationForm" className="modern-btn primary sm mt-3 w-100">Apply Now</Link>
                </div>
              </div>
            </RevealItem>

            <RevealItem as="div" direction="zoom" className="col-lg-4 col-md-6">
              <div className="reveal-card va-hover-lift">
                <div className="reveal-bg" style={{backgroundImage: "url('https://images.unsplash.com/photo-1505761671935-60b3a7427bad?w=500&h=600&fit=crop')"}}></div>
                <div className="reveal-overlay"></div>
                <div className="reveal-content-front">
                  <div className="flag-icon mb-2">🇬🇧</div>
                  <h3>United Kingdom</h3>
                </div>
                <div className="reveal-content-hover">
                  <h3>UK</h3>
                  <p>Historic universities with 1-year master's programs.</p>
                  <ul className="list-unstyled small mt-3">
                    <li><MdCheckCircle className="text-success me-2" />Global Recognition</li>
                    <li><MdCheckCircle className="text-success me-2" />2-Year Graduate Route</li>
                    <li><MdCheckCircle className="text-success me-2" />Rich Culture</li>
                  </ul>
                  <Link to="/consultationForm" className="modern-btn primary sm mt-3 w-100">Apply Now</Link>
                </div>
              </div>
            </RevealItem>

            <RevealItem as="div" direction="zoom" className="col-lg-4 col-md-6">
              <div className="reveal-card va-hover-lift">
                <div className="reveal-bg" style={{backgroundImage: "url('https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?w=500&h=600&fit=crop')"}}></div>
                <div className="reveal-overlay"></div>
                <div className="reveal-content-front">
                  <div className="flag-icon mb-2">🇺🇸</div>
                  <h3>USA</h3>
                </div>
                <div className="reveal-content-hover">
                  <h3>USA</h3>
                  <p>Ivy League excellence and a massive tech industry.</p>
                  <ul className="list-unstyled small mt-3">
                    <li><MdCheckCircle className="text-success me-2" />Top Rankings</li>
                    <li><MdCheckCircle className="text-success me-2" />Innovation Hub</li>
                    <li><MdCheckCircle className="text-success me-2" />3-Year STEM OPT</li>
                  </ul>
                  <Link to="/consultationForm" className="modern-btn primary sm mt-3 w-100">Apply Now</Link>
                </div>
              </div>
            </RevealItem>

            <RevealItem as="div" direction="zoom" className="col-lg-4 col-md-6">
              <div className="reveal-card va-hover-lift">
                <div className="reveal-bg" style={{backgroundImage: "url('https://images.unsplash.com/photo-1467269204594-9661b134dd2b?w=500&h=600&fit=crop')"}}></div>
                <div className="reveal-overlay"></div>
                <div className="reveal-content-front">
                  <div className="flag-icon mb-2">🇩🇪</div>
                  <h3>Germany</h3>
                </div>
                <div className="reveal-content-hover">
                  <h3>Germany</h3>
                  <p>Tuition-free education at leading public universities.</p>
                  <ul className="list-unstyled small mt-3">
                    <li><MdCheckCircle className="text-success me-2" />No/Low Tuition Fees</li>
                    <li><MdCheckCircle className="text-success me-2" />Engineering Excellence</li>
                    <li><MdCheckCircle className="text-success me-2" />18 Months Job Search</li>
                  </ul>
                  <Link to="/consultationForm" className="modern-btn primary sm mt-3 w-100">Apply Now</Link>
                </div>
              </div>
            </RevealItem>

            <RevealItem as="div" direction="zoom" className="col-lg-4 col-md-6">
              <div className="reveal-card va-hover-lift">
                <div className="reveal-bg" style={{backgroundImage: "url('https://images.unsplash.com/photo-1507699622108-4be3abd695ad?w=500&h=600&fit=crop')"}}></div>
                <div className="reveal-overlay"></div>
                <div className="reveal-content-front">
                  <div className="flag-icon mb-2">🇳🇿</div>
                  <h3>New Zealand</h3>
                </div>
                <div className="reveal-content-hover">
                  <h3>New Zealand</h3>
                  <p>Safest environment with excellent work-life balance.</p>
                  <ul className="list-unstyled small mt-3">
                    <li><MdCheckCircle className="text-success me-2" />Safe Environment</li>
                    <li><MdCheckCircle className="text-success me-2" />Up to 3 Years Post-Study Work</li>
                    <li><MdCheckCircle className="text-success me-2" />Stunning Nature</li>
                  </ul>
                  <Link to="/consultationForm" className="modern-btn primary sm mt-3 w-100">Apply Now</Link>
                </div>
              </div>
            </RevealItem>
          </RevealGroup>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-6 section-services">
        <div className="container">
          <Reveal as="h2" direction="up" className="text-center mb-5 display-5 fw-bold">
            Comprehensive Support
          </Reveal>
          <RevealGroup as="div" className="row g-4" stagger={0.1}>
            <RevealItem as="div" className="col-md-3 col-sm-6">
              <div className="service-card-modern va-hover-lift p-4 h-100">
                <div className="service-icon-wrapper">
                  <MdOutlineMenuBook className="service-icon" />
                </div>
                <h4 className="mb-3">Course Selection</h4>
                <p className="text-muted mb-0">Personalized counseling to shortlist programs matching your background.</p>
              </div>
            </RevealItem>
            <RevealItem as="div" className="col-md-3 col-sm-6">
              <div className="service-card-modern va-hover-lift p-4 h-100">
                <div className="service-icon-wrapper">
                  <MdDescription className="service-icon" />
                </div>
                <h4 className="mb-3">Application</h4>
                <p className="text-muted mb-0">Complete support for applications, essays, and required documentation.</p>
              </div>
            </RevealItem>
            <RevealItem as="div" className="col-md-3 col-sm-6">
              <div className="service-card-modern va-hover-lift p-4 h-100">
                <div className="service-icon-wrapper">
                  <FaHandshake className="service-icon" />
                </div>
                <h4 className="mb-3">Scholarships</h4>
                <p className="text-muted mb-0">Expert guidance in finding and applying for financial aid and scholarships.</p>
              </div>
            </RevealItem>
            <RevealItem as="div" className="col-md-3 col-sm-6">
              <div className="service-card-modern va-hover-lift p-4 h-100">
                <div className="service-icon-wrapper">
                  <FaPlane className="service-icon" />
                </div>
                <h4 className="mb-3">Visa Support</h4>
                <p className="text-muted mb-0">Complete visa documentation and interview preparation assistance.</p>
              </div>
            </RevealItem>
          </RevealGroup>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-6 bg-light-mesh">
        <div className="container">
          <Reveal as="h2" direction="up" className="text-center mb-5 display-5 fw-bold">
            Frequently Asked Questions
          </Reveal>
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="faq-container-modern">
                {studyFaqs.map((faq, i) => (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    key={i} 
                    className={`faq-item-modern ${openFaq === i ? 'active' : ''}`}
                  >
                    <div className="faq-header" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                      <h5 className="mb-0">{faq.q}</h5>
                      <span className="faq-toggle">{openFaq === i ? '-' : '+'}</span>
                    </div>
                    <AnimatePresence>
                      {openFaq === i && (
                        <motion.div 
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="faq-body"
                        >
                          <p className="text-muted mb-0 mt-3">{faq.a}</p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Split */}
      <CtaBand
        title="Not sure which country fits your marks and budget?"
        body="Bring your transcripts and a rough budget to a free sitting. You will leave with a shortlist of universities that are realistic for your profile — and an honest view of the ones that are not."
        primary={{ to: "/consultationForm", label: "Book a free counselling session" }}
        secondary={{ to: "/faq", label: "Read common questions" }}
      />
    </div>
  );
}